/**
 * Calendar screen (Layout A).
 *
 * Left column: an inline month calendar control (month + year nav, DOW row,
 * day grid; today gets an accent ring, the selected day an accent fill).
 * Right column: the selected day's activity, grouped into four sections in
 * order — Created, Due, Closed, Updated. A task may appear in more than one
 * section (e.g. created and closed the same day) — that's intended.
 *
 * Rows within each section are ordered by the screen's sort control, which
 * defaults to `native` (the server's own order) because this is a date-driven
 * view where chronological reading order carries meaning. The section order
 * itself never changes.
 *
 * The day range is resolved server-side (GET /api/tasks/calendar?date=…); we
 * just send a YYYY-MM-DD string derived from the locally selected day.
 */

import { useMemo, useState } from 'react';
import { useTasksCalendar } from '../api/tasks';
import { TaskSection } from '../components/TaskSection';
import { useSectionSorts } from '../lib/task-sort';
import { useToggleTask } from './_use-open-task';
import { useTaskQuickActions } from './_use-task-quick-actions';
import lists from './Lists.module.css';
import styles from './Calendar.module.css';

interface Ymd {
  y: number;
  m: number;
  d: number;
}

const DOW = ['S', 'M', 'T', 'W', 'T', 'F', 'S'];
const MONTHS = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
];

/** Serialise a local y/m/d to the YYYY-MM-DD string the endpoint expects. */
function ymdToStr({ y, m, d }: Ymd): string {
  return `${y}-${String(m + 1).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
}

/** Day-of-week index (0=Sun) for the given local y/m/d. */
function dayOfWeek(y: number, m: number, d: number): number {
  return new Date(y, m, d).getDay();
}

export function CalendarScreen() {
  const today = useMemo<Ymd>(() => {
    const n = new Date();
    return { y: n.getFullYear(), m: n.getMonth(), d: n.getDate() };
  }, []);

  // The selected day drives the query; the calendar `view` (visible month) is
  // separate so paging months doesn't change the selection until a day is
  // clicked. Both start on today.
  const [selected, setSelected] = useState<Ymd>(today);
  const [view, setView] = useState<{ y: number; m: number }>({ y: today.y, m: today.m });

  const dateStr = ymdToStr(selected);
  const query = useTasksCalendar(dateStr);
  const complete = useToggleTask();
  const quick = useTaskQuickActions();
  const sorts = useSectionSorts({
    'calendar.created': 'native',
    'calendar.due': 'native',
    'calendar.closed': 'native',
    'calendar.updated': 'native',
  });

  const monthLabel = `${MONTHS[view.m]} ${view.y}`;
  const firstDay = dayOfWeek(view.y, view.m, 1);
  const daysInMonth = new Date(view.y, view.m + 1, 0).getDate();

  const cells: Array<number | null> = [];
  for (let i = 0; i < firstDay; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(d);
  while (cells.length % 7 !== 0) cells.push(null);

  const isToday = (d: number) => today.y === view.y && today.m === view.m && d === today.d;
  const isSelected = (d: number) =>
    selected.y === view.y && selected.m === view.m && d === selected.d;

  function stepMonth(delta: number) {
    setView((v) => {
      const total = v.y * 12 + v.m + delta;
      return { y: Math.floor(total / 12), m: ((total % 12) + 12) % 12 };
    });
  }
  function stepYear(delta: number) {
    setView((v) => ({ ...v, y: v.y + delta }));
  }
  function pickDay(d: number) {
    setSelected({ y: view.y, m: view.m, d });
  }

  const data = query.data;
  const due = data?.due ?? [];
  const closed = data?.closed ?? [];
  const created = data?.created ?? [];
  const updated = data?.updated ?? [];
  const total = due.length + closed.length + created.length + updated.length;

  // Full-date heading, e.g. "Monday · 27 July 2026".
  const selectedDate = new Date(selected.y, selected.m, selected.d);
  const weekday = selectedDate.toLocaleDateString(undefined, { weekday: 'long' });
  const longDate = selectedDate.toLocaleDateString(undefined, {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });

  // Count tiles jump to their group in the list below (no-op when the group
  // is empty — that section isn't rendered so there's no anchor to hit).
  const scrollToSection = (key: string) => {
    document
      .getElementById(`sec-${key}`)
      ?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  return (
    <div className={styles.page}>
      <div className={styles.top}>
      {/* Left: month calendar control */}
      <div className={styles.calWrap}>
        <div className={styles.cal}>
          <div className={styles.calHead}>
            <span className={styles.calMonth}>{monthLabel}</span>
            <div className={styles.calNavGroup}>
              <button
                type="button"
                className={styles.calNav}
                onClick={() => stepYear(-1)}
                aria-label="Previous year"
              >
                «
              </button>
              <button
                type="button"
                className={styles.calNav}
                onClick={() => stepMonth(-1)}
                aria-label="Previous month"
              >
                ‹
              </button>
              <button
                type="button"
                className={styles.calNav}
                onClick={() => stepMonth(1)}
                aria-label="Next month"
              >
                ›
              </button>
              <button
                type="button"
                className={styles.calNav}
                onClick={() => stepYear(1)}
                aria-label="Next year"
              >
                »
              </button>
            </div>
          </div>

          <div className={`${styles.calGrid} ${styles.calDow}`}>
            {DOW.map((d, i) => (
              <span key={i}>{d}</span>
            ))}
          </div>

          <div className={styles.calGrid}>
            {cells.map((d, i) =>
              d === null ? (
                <span key={i} />
              ) : (
                <button
                  key={i}
                  type="button"
                  className={`${styles.calDay} ${isToday(d) ? styles.calDayToday : ''} ${
                    isSelected(d) ? styles.calDaySelected : ''
                  }`}
                  onClick={() => pickDay(d)}
                  aria-pressed={isSelected(d)}
                >
                  {d}
                </button>
              ),
            )}
          </div>
        </div>
      </div>

      {/* Right: selected day + per-group count tiles (stretches to match
          the calendar's height). */}
      <div className={styles.summary}>
        <div className={styles.summaryHead}>
          <div className={styles.eyebrow}>Selected day</div>
          <h2 className={styles.dayTitle}>
            {weekday} · {longDate}
          </h2>
          <div className={styles.daySub}>
            {query.isLoading
              ? 'Loading…'
              : query.isError
                ? 'Couldn’t load this day.'
                : total === 0
                  ? 'No activity'
                  : `${total} ${total === 1 ? 'activity' : 'activities'}`}
          </div>
        </div>
        <div className={styles.tiles}>
          <Tile label="Created" count={created.length} tone="created" onClick={() => scrollToSection('created')} />
          <Tile label="Due" count={due.length} tone="due" onClick={() => scrollToSection('due')} />
          <Tile label="Closed" count={closed.length} tone="closed" onClick={() => scrollToSection('closed')} />
          <Tile label="Updated" count={updated.length} tone="updated" onClick={() => scrollToSection('updated')} />
        </div>
      </div>
      </div>

      {/* Bottom: the day's tasks, grouped */}
      <div className={styles.bottom}>
        {query.isLoading ? (
          <div className={lists.loading}>Loading…</div>
        ) : query.isError ? (
          <div className={lists.error}>Couldn’t load this day.</div>
        ) : total === 0 ? (
          <div className={lists.empty}>Nothing on this day.</div>
        ) : (
          <>
            <TaskSection
              id="sec-created"
              title="Created"
              tasks={created}
              sort={sorts.sortFor('calendar.created')}
              onSortChange={(next) => sorts.setSortFor('calendar.created', next)}
              onComplete={complete}
              quick={quick}
            />
            <TaskSection
              id="sec-due"
              title="Due"
              tasks={due}
              sort={sorts.sortFor('calendar.due')}
              onSortChange={(next) => sorts.setSortFor('calendar.due', next)}
              onComplete={complete}
              quick={quick}
            />
            <TaskSection
              id="sec-closed"
              title="Closed"
              tasks={closed}
              showStatus
              sort={sorts.sortFor('calendar.closed')}
              onSortChange={(next) => sorts.setSortFor('calendar.closed', next)}
              onComplete={complete}
              quick={quick}
            />
            <TaskSection
              id="sec-updated"
              title="Updated"
              tasks={updated}
              sort={sorts.sortFor('calendar.updated')}
              onSortChange={(next) => sorts.setSortFor('calendar.updated', next)}
              onComplete={complete}
              quick={quick}
            />
          </>
        )}
      </div>
    </div>
  );
}

const TILE_TONE: Record<'due' | 'closed' | 'created' | 'updated', string> = {
  due: styles.tileDue!,
  closed: styles.tileClosed!,
  created: styles.tileCreated!,
  updated: styles.tileUpdated!,
};

function Tile({
  label,
  count,
  tone,
  onClick,
}: {
  label: string;
  count: number;
  tone: 'due' | 'closed' | 'created' | 'updated';
  onClick: () => void;
}) {
  return (
    <button type="button" className={`${styles.tile} ${TILE_TONE[tone]}`} onClick={onClick}>
      <span className={styles.tileLabel}>
        <span className={styles.tileDot} />
        {label}
      </span>
      <span className={styles.tileValue}>{count}</span>
    </button>
  );
}
